/* eslint-disable @typescript-eslint/camelcase, camelcase */
import { createAsyncAction, createReducer, ActionType, createAction } from 'typesafe-actions';
import { log404ErrorAsWarning } from '../../../common/apiAdapters/ErrorHandlers';
import {
    FetchAction,
    SuccessFetchActionPayload,
    DefaultErrorPayload,
    defaultErrorHandler,
} from '../dataFetching/generators';
import { DataFetchingThunkAction } from '../types';
import { ColorVariantsState, ColorVariantsAPIResponse, Simple } from './types';
import { getSimpleColorVariants, mapApiColorVariantsToReducerColorVariants } from './helper';
import { setPLPColorVariants } from 'Client/redux/products/actions';

// Actions
export const colorVariantsDefaultState: ColorVariantsState = {
    data: [],
    didInvalidate: false,
    identifier: '',
    isFetching: false,
};

export const getColorVariants = createAction('@PRODUCT_DETAILS/COLOR_VARIANTS/GET_COLOR_VARIANTS', action => {
    return (identifier: string, simple: Simple) => action({ identifier, simple });
});

export const fetchColorVariantsForSimpleAction = createAsyncAction(
    '@PRODUCT_DETAILS/COLOR_VARIANTS/FETCH_START',
    '@PRODUCT_DETAILS/COLOR_VARIANTS/FETCH_SUCCESS',
    '@PRODUCT_DETAILS/COLOR_VARIANTS/FETCH_FAILED'
)<FetchAction, SuccessFetchActionPayload<ColorVariantsAPIResponse>, DefaultErrorPayload>();

export const colorVariantsActions = [
    fetchColorVariantsForSimpleAction.failure,
    fetchColorVariantsForSimpleAction.request,
    fetchColorVariantsForSimpleAction.success,
    getColorVariants,
];

export type ColorVariantsActionsType =
    | ActionType<typeof fetchColorVariantsForSimpleAction.failure>
    | ActionType<typeof fetchColorVariantsForSimpleAction.request>
    | ActionType<typeof fetchColorVariantsForSimpleAction.success>
    | ActionType<typeof getColorVariants>;

export const fetchProductSimpleColorVariants = (
    urlKey: string,
    simpleSku: string,
    source?: 'PLP' | 'PDP'
): DataFetchingThunkAction => async (dispatch, getState, { apiService, logger }) => {
    const identifier = simpleSku;

    if (
        source === 'PLP' &&
        getState().products.products.find(product => product.simpleSku === identifier)?.colorVariants?.length
    ) {
        return;
    }
    if (source !== 'PLP' && identifier === getState().productdetails.colorVariants.identifier) {
        return;
    }

    dispatch(fetchColorVariantsForSimpleAction.request({ identifier }));
    try {
        const colorVariants = await apiService.shopApi.getProductColorVariantsBySimpleSku(urlKey, simpleSku);
        if (source === 'PLP') {
            dispatch(setPLPColorVariants(colorVariants.data, identifier));
        }
        dispatch(fetchColorVariantsForSimpleAction.success({ data: colorVariants, identifier }));
    } catch (err) {
        log404ErrorAsWarning(err, identifier, logger, 'fetchProductSimpleColorVariants');
        dispatch(fetchColorVariantsForSimpleAction.failure({ error: err, identifier }));
    }
};

export const colorVariantsReducer = createReducer<ColorVariantsState, ColorVariantsActionsType>(
    colorVariantsDefaultState
)
    .handleAction(getColorVariants, (state, action) => {
        const {
            payload: { identifier, simple },
        } = action;
        const colorVariants = getSimpleColorVariants(simple, identifier);
        return {
            ...state,
            data: colorVariants,
            identifier,
        };
    })
    .handleAction(fetchColorVariantsForSimpleAction.failure, (state, action) => defaultErrorHandler(state, action))
    .handleAction(fetchColorVariantsForSimpleAction.request, (state, action) => ({
        ...state,
        isFetching: true,
        identifier: action.payload.identifier,
        didInvalidate: false,
        error: undefined,
    }))
    .handleAction(fetchColorVariantsForSimpleAction.success, (state, action) => {
        const {
            payload: {
                data: { data },
                identifier,
            },
        } = action;

        const colorVariants = mapApiColorVariantsToReducerColorVariants(data, identifier);
        return {
            ...state,
            data: colorVariants,
            identifier,
            isFetching: false,
        };
    });
